import { useContext } from 'react';
import { Context } from '../../../App';

const fmt = (v, digits = 2) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) {
    return '—';
  }
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
};

export default function ImmediatePileRockSketch({ name, Q, socketLength, socketDiameter, ec, em, ips, variant }) {
  const { theme: appTheme } = useContext(Context);

  // Viewbox dimensions (logical units)
  const VW = 820;
  const VH = 520;

  // Layout constants
  const margin = 20;
  const groundY = 50; // ground surface y
  const interfaceY = 300; // clay–rock interface
  const bottomY = VH - margin;

  // Pile/cap geometry in svg units (illustrative, not to scale with inputs)
  const capWidth = 220;
  const capHeight = 30;
  const capX = VW / 2 - capWidth / 2;
  const capY = groundY + 35;

  const columnWidth = 40;
  const columnHeight = 65;
  const columnX = VW / 2 - columnWidth / 2;
  const columnY = groundY - capHeight;

  // Two piles
  const pileWidth = 30;
  const pileTopY = capY + capHeight;
  const pileSpacing = 95;
  const pile1X = VW / 2 - pileSpacing / 2 - pileWidth / 2;
  const pile2X = VW / 2 + pileSpacing / 2 - pileWidth / 2;

  // Socket depth rendering scale: map socketLength (m) to pixels below interface
  // Clamp to sensible range for drawing (min 20px, max 120px)
  const pxPerM = 35; // heuristic for depiction
  const socketDepthPxRaw = Number(socketLength) > 0 ? Number(socketLength) * pxPerM : 0;
  const socketDepthPx = Math.max(20, Math.min(120, socketDepthPxRaw || 0));

  // Draw socketed part below interface for both piles
  const pileEmbedBottom = Math.min(bottomY - 20, interfaceY + socketDepthPx);

  // Show placeholder if critical inputs missing
  const hasCore = !Q && !socketLength && !socketDiameter && !ec && !em && !ips;

  // Colors (neutral greys)
  const theme = variant === 'pdf-light' ? 'light' : appTheme;

  const lineColor = theme === 'light' ? '#222' : '#878787ff';
  const textColor = theme === 'light' ? '#222' : '#fff';
  const concreteFillColor = theme === 'light' ? '#fff' : '#282828ff';
  const soilFillColor = theme === 'light' ? '#999' : '#ffffffff';

  return (
    <div class="sketch-rail">
      <div class="sketch-wrap">
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          width="100%"
          role="img"
          aria-label="Pile settlement sketch"
          style={{ display: 'block', maxWidth: '980px', margin: '0 auto' }}
        >
          <defs>
            {/* Clay dot texture */}
            <pattern id="clayDots" patternUnits="userSpaceOnUse" width="8" height="8">
              <circle cx="1" cy="1" r="0.6" fill={soilFillColor} />
            </pattern>
            {/* Rock hatch */}
            <pattern id="rockHatch" patternUnits="userSpaceOnUse" width="30" height="30">
              <rect width="20" height="20" fill="none" />
              {/* main diagonal fractures */}
              <path
                d="
      M0 12 L12 0
      M4 30 L30 4
      M-4 8 L8 -4
    "
                stroke="#777"
                strokeWidth="0.5"
              />
              {/* secondary opposite-direction fractures */}
              <path
                d="
      M0 4 L12 30
      M8 0 L30 8
    "
                stroke="#999"
                strokeWidth="0.3"
              />
            </pattern>

            {/* Arrow marker */}
            <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
            {/* Double arrow heads for dimensions */}
            <marker id="arrowEnd" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
            <marker
              id="arrowStart"
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={lineColor} />
            </marker>
          </defs>

          {/* Ground surface */}
          <line x1={margin} y1={groundY} x2={VW - margin} y2={groundY} stroke={lineColor} strokeWidth="1.2" />
          <text x={margin + 10} y={groundY - 10} fontSize="14" fill={textColor}>
            Ground Surface
          </text>
          {/* Bottom Line */}
          <line x1={margin} y1={bottomY} x2={VW - margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />
          {/* Left Line */}
          <line x1={margin} y1={groundY} x2={margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />
          {/* Right Line */}
          <line x1={VW - margin} y1={groundY} x2={VW - margin} y2={bottomY} stroke={lineColor} strokeWidth="1.2" />
          {/* Clay layer */}
          <rect
            x={margin}
            y={groundY}
            width={VW - 2 * margin}
            height={interfaceY - groundY}
            fill="url(#clayDots)"
            opacity={hasCore ? 1 : 0.3}
          />
          <text x={margin + 10} y={groundY + 40} fontSize="16" fill={textColor}>
            Soil
          </text>

          {/* Rock layer */}
          <rect
            x={margin}
            y={interfaceY}
            width={VW - 2 * margin}
            height={bottomY - interfaceY}
            fill="url(#rockHatch)"
            opacity={hasCore ? 1 : 0.3}
          />
          <text x={margin + 10} y={interfaceY + 40} fontSize="16" fill={textColor}>
            Rock
          </text>

          {/* Interface line */}
          <line x1={margin} y1={interfaceY} x2={VW - margin} y2={interfaceY} stroke={lineColor} strokeWidth="1" />

          {/* Column */}
          <rect
            x={columnX}
            y={columnY}
            width={columnWidth}
            height={columnHeight}
            fill={concreteFillColor}
            stroke={lineColor}
            strokeWidth="1"
          />

          {/* Pile cap */}
          <rect
            x={capX}
            y={capY}
            width={capWidth}
            height={capHeight}
            fill={concreteFillColor}
            stroke={lineColor}
            strokeWidth="1"
          />

          {/* Two piles */}
          {[pile1X, pile2X].map((px, idx) => (
            <g key={idx}>
              {/* Pile through clay */}
              <rect
                x={px}
                y={pileTopY}
                width={pileWidth}
                height={Math.max(0, interfaceY - pileTopY)}
                fill={concreteFillColor}
                stroke={lineColor}
                strokeWidth="1"
              />
              {/* Pile socket in rock */}
              <rect
                x={px}
                y={interfaceY}
                width={pileWidth}
                height={Math.max(0, pileEmbedBottom - interfaceY)}
                fill={concreteFillColor}
                stroke={lineColor}
                strokeWidth="1"
              />
              {/* Optional centerline */}
              <line
                x1={px + pileWidth / 2}
                y1={pileTopY + 10}
                x2={px + pileWidth / 2}
                y2={pileEmbedBottom - 10}
                stroke="#b44"
                strokeWidth="1"
                strokeDasharray="6 6"
                opacity="0.6"
              />
            </g>
          ))}

          {/* Load arrow Q on pile cap */}
          {Q && (
            <>
              <g>
                <line
                  x1={pile2X + pileWidth / 2}
                  y1={capY - 15}
                  x2={pile2X + pileWidth / 2}
                  y2={capY + 18}
                  stroke={lineColor}
                  strokeWidth="1.6"
                  markerEnd="url(#arrow)"
                />
                <text x={pile2X + pileWidth / 2 + 5} y={capY - 20} fontSize="14" fill={textColor}>
                  {`Q = ${Q} MN `}
                </text>
                <text x={pile2X + pileWidth / 2 + 5} y={capY - 5} fontSize="12" fill={textColor}>
                  {`(Total axial compression load applied to shaft butt)`}
                </text>
              </g>
            </>
          )}

          {/* Ec label in clay */}
          {ec && (
            <g>
              <line
                x1={pile2X + pileWidth / 2}
                y1={(pileTopY + interfaceY) / 2}
                x2={pile2X + pileWidth + 60}
                y2={(pileTopY + interfaceY) / 2 - 20}
                stroke={lineColor}
                strokeWidth="1"
                markerEnd="url(#arrow)"
              />
              <text x={pile2X + pileWidth + 65} y={(pileTopY + interfaceY) / 2 - 24} fontSize="14" fill={textColor}>
                {`Ec = ${fmt(ec, 0)} MPa`}
              </text>
              <text x={pile2X + pileWidth + 65} y={(pileTopY + interfaceY) / 2 - 5} fontSize="12" fill={textColor}>
                {`(Elastic Modulus of concrete)`}
              </text>
            </g>
          )}

          {/* Em label in rock */}
          {em && (
            <g>
              <text x={pile2X + pileWidth + 130} y={(interfaceY + pileEmbedBottom) / 2} fontSize="14" fill={textColor}>
                {`Em = ${fmt(em, 0)} MPa`}
              </text>
              <text x={pile2X + pileWidth + 130} y={(interfaceY + pileEmbedBottom) / 2 + 20} fontSize="12" fill={textColor}>
                {`(Elastic Modulus of Rock Mass)`}
              </text>
            </g>
          )}

          {/* Dr dimension (socketLength) */}
          {socketLength && (
            <g>
              {/* dashed extension line */}
              <line
                x1={pile1X - 30}
                y1={interfaceY}
                x2={pile1X - 30}
                y2={pileEmbedBottom}
                stroke={lineColor}
                strokeWidth="1"
                strokeDasharray="5 4"
              />
              {/* dimension line with double arrows */}
              <line
                x1={pile1X - 30}
                y1={interfaceY + 3}
                x2={pile1X - 30}
                y2={pileEmbedBottom - 5}
                stroke={lineColor}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arrowEnd)"
              />
              <text
                x={pile1X - 85}
                y={(interfaceY + pileEmbedBottom) / 2 - 15}
                fill={textColor}
                transform={`rotate(-90 ${pile1X - 48},${(interfaceY + pileEmbedBottom) / 2})`}
              >
                {`Dr = ${fmt(socketLength, 2)} m`}
              </text>
              <text
                x={pile1X - 85}
                y={(interfaceY + pileEmbedBottom) / 2 + 5}
                fill={textColor}
                transform={`rotate(-90 ${pile1X - 48},${(interfaceY + pileEmbedBottom) / 2})`}
                fontSize="12"
              >
                {`(Socket Length)`}
              </text>
            </g>
          )}

          {/* Br dimension (socketDiameter) at tip */}
          {socketDiameter && (
            <g>
              <line
                x1={pile2X + 2}
                y1={pileEmbedBottom + 14}
                x2={pile2X + pileWidth - 2}
                y2={pileEmbedBottom + 14}
                stroke={lineColor}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arrowEnd)"
              />
              <text x={VW / 2 + 70} y={pileEmbedBottom + 18} fontSize="14" fill={textColor}>{`Br = ${fmt(
                socketDiameter,
                2
              )} m`}</text>
              <text x={VW / 2 + 70} y={pileEmbedBottom + 38} fontSize="12" fill={textColor}>{`(Socket Diameter)`}</text>
            </g>
          )}

          {/* Placeholder when inputs are missing */}
          {hasCore && (
            <g opacity="0.9">
              <rect x={VW / 2 - 210} y={groundY + 90} width="420" height="60" rx="8" fill={concreteFillColor} stroke="#aaa" />
              <text x={VW / 2} y={groundY + 125} textAnchor="middle" fontSize="14" fill={textColor}>
                Enter pile load and material properties to generate the sketch
              </text>
            </g>
          )}
          <text x={VW / 2} y={bottomY - 4} textAnchor="middle" fontSize="11" fill={textColor} opacity="0.8">
            Note: The calculation is for the settlement of a single pile. Two piles are illustrated in the sketch for clarity of
            presentation.
          </text>
        </svg>
      </div>
    </div>
  );
}
